/**
 * Inicio + Dashboard summary for the remesas gestor.
 * Shows quick KPIs and shortcuts.
 */
import { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Link } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Send, Clock, XCircle, History, ArrowLeftRight, CreditCard, Users, Ticket, Wallet } from 'lucide-react';
import { listOperationsByType } from '@/services/remittanceApi';
import StateBadge from '@/components/workflow/StateBadge';

const TERMINAL_STATES = new Set(['DELIVERED', 'CANCELLED', 'REJECTED', 'FAILED', 'COMPLETED']);

export const GestorRemesasInicioPage = () => {
  const { user, getAuthHeaders } = useAuth();
  const [stats, setStats] = useState({ total: 0, pending: 0, rejected: 0, delivered: 0 });
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const r = await listOperationsByType(getAuthHeaders, { type: 'remittance', limit: 200 });
        const ops = r.operations || [];
        const total = ops.length;
        const pending = ops.filter((o) => !TERMINAL_STATES.has(o.state)).length;
        const rejected = ops.filter((o) => o.state === 'REJECTED' || o.state === 'FAILED' || o.state === 'CANCELLED').length;
        const delivered = ops.filter((o) => o.state === 'DELIVERED' || o.state === 'COMPLETED').length;
        setStats({ total, pending, rejected, delivered });
        setRecent(ops.slice(0, 5));
      } catch (e) {
        // Soft-fail: empty stats panel is acceptable; surface in console for debug.
        // eslint-disable-next-line no-console
        console.warn('[GestorRemesasInicio] load failed', e);
      }
    })();
  }, [getAuthHeaders]);

  const QUICK = [
    { to: '/remesas/enviar',        Icon: Send,           label: 'Enviar Remesa' },
    { to: '/remesas/mis-pasajeros', Icon: Ticket,         label: 'Mis Pasajeros' },
    { to: '/remesas/mi-wallet',     Icon: Wallet,         label: 'Mi Wallet' },
    { to: '/remesas/historial',     Icon: History,        label: 'Historial' },
  ];

  return (
    <div className="space-y-6" data-testid="gestor-inicio-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Hola, {user?.name || 'Gestor'}</h1>
        <p className="text-xs text-slate-500 mt-1">Tu resumen operacional de remesas.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'Total operaciones', value: stats.total,     color: 'text-slate-900', Icon: History },
          { label: 'Pendientes',        value: stats.pending,   color: 'text-amber-700', Icon: Clock },
          { label: 'Entregadas',        value: stats.delivered, color: 'text-emerald-700', Icon: Send },
          { label: 'Rechazadas',        value: stats.rejected,  color: 'text-rose-700', Icon: XCircle },
        ].map((s) => (
          <Card key={s.label} className="border-0 shadow-sm p-4">
            <div className="flex items-center justify-between">
              <p className="text-[10px] uppercase tracking-wider text-slate-500">{s.label}</p>
              <s.Icon className="w-4 h-4 text-slate-400" />
            </div>
            <p className={`text-3xl font-bold ${s.color} mt-1`}>{s.value}</p>
          </Card>
        ))}
      </div>

      <div>
        <h2 className="text-sm font-semibold text-slate-700 mb-2">Accesos rápidos</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {QUICK.map((q) => (
            <Link key={q.to} to={q.to} className="border rounded-lg p-4 bg-white hover:border-amber-400 hover:shadow-sm transition-all" data-testid={`shortcut-${q.to.split('/').pop()}`}>
              <q.Icon className="w-5 h-5 text-amber-600 mb-1" />
              <p className="font-semibold text-sm text-slate-900">{q.label}</p>
            </Link>
          ))}
        </div>
      </div>

      <Card className="border-0 shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b bg-slate-50 flex items-center justify-between">
          <p className="text-sm font-semibold">Operaciones recientes</p>
          <Link to="/remesas/historial" className="text-xs text-amber-700 hover:underline">Ver todas</Link>
        </div>
        {recent.length === 0 ? (
          <p className="p-8 text-center text-slate-400 text-sm">Aún no has enviado remesas. <Link to="/remesas/enviar" className="text-amber-700 underline">Crear la primera</Link>.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b text-left text-xs text-slate-500 uppercase">
                <th className="p-3">Beneficiario</th>
                <th className="p-3">Monto</th>
                <th className="p-3">Estado</th>
                <th className="p-3">Fecha</th>
              </tr>
            </thead>
            <tbody>
              {recent.map((o) => (
                <tr key={o.id} className="border-b hover:bg-slate-50">
                  <td className="p-3">{o.metadata?.beneficiary_name || '—'}</td>
                  <td className="p-3 font-mono text-emerald-700">${o.amount?.toFixed(2)} {o.currency_from}</td>
                  <td className="p-3"><StateBadge state={o.state} /></td>
                  <td className="p-3 text-slate-400 text-xs">{o.created_at && new Date(o.created_at).toLocaleString('es-ES')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Card className="border-0 shadow-sm p-4 bg-amber-50 border-l-4 border-amber-400">
        <div className="flex items-start gap-3">
          <Users className="w-5 h-5 text-amber-600 mt-0.5" />
          <div className="text-sm">
            <p className="font-semibold text-amber-900">Tu panel está activo</p>
            <p className="text-xs text-amber-700 mt-1">
              Las secciones Lista Promotores, Trabajadores, Usuarios, FAQ y Soporte se habilitarán en la siguiente fase. Por ahora puedes ya operar Remesas, Cuentas, Historial y Conversor.
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default GestorRemesasInicioPage;
